import moment from 'moment-timezone';
import BaseModel, { IResourceName } from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';
import { getBufferZoneCriticality } from 'volta/utils/style-utils';
import { hashCode } from 'volta/utils/text-utils';

import { attr } from '@ember-data/model';

import { TAcquisitionCode } from './constants/acquisition-codes';
import {
  IChangeManyRoadmaps, IChangeManyStatus, IChangeOneStatus, IChangeRoadmap, ICreateDraftOrder,
  ICreateDraftOrderFromSku, IDraftPlanningProjections, IManyPlanningOrdersUpdatedResult,
  IPlanningOrderSKU,
  ISchedulingAlertSummary,
  ISyncAlertSummary, IUpdateExternalData, IUpdateOrderNum, IUpdatePlanningOrder,
  TPOStatus,
  TSyncAlerts
} from './types/planning-order';
import { IAggTasksViewMap } from './types/tasks.d';
import { IWorkshopIdentityInfo } from './types/workshop';
import { ISupplierIdentityInfo } from "volta/models/types/supplier";
import { IWarehouseIdentityInfo } from "volta/models/types/warehouse";
import { HookClass } from "volta/utils/api/types";
import { ICmdResult } from 'volta/utils/api/jsonapi-types';

export interface IPOKeyObject {
  orderHash: string;
  orderKey: string;
  num?: string;
  warehouse: IWarehouseIdentityInfo;
  acqCode: TAcquisitionCode;
  supplier?: ISupplierIdentityInfo;
  workshop?: IWorkshopIdentityInfo;
  deliveryWorkshop?: IWorkshopIdentityInfo;
  externalOrderNum?: string;
}

export const SyncAlerts: Record<TSyncAlerts, number> = {
  CRITICAL: 0,
  WARNING: 1,
  NONE: 2
};

export const Statuses: Record<TPOStatus, TPOStatus> = {
  NEW: 'NEW',
  DRAFT: 'DRAFT',
  LAUNCHED: 'LAUNCHED',
  COMMITTED: 'COMMITTED',
  SENT: 'SENT',
  FINISHED: 'FINISHED',
  CANCELLED: 'CANCELLED',
  PROJECTED: 'PROJECTED',
  PROJECTED_FIRM: 'PROJECTED_FIRM'
};

const HiddenStatuses = [
  Statuses.NEW,
  Statuses.FINISHED,
  Statuses.CANCELLED,
  Statuses.PROJECTED,
  Statuses.PROJECTED_FIRM
];

export const FirmPOStatuses: TPOStatus[] = [
  Statuses.LAUNCHED,
  Statuses.FINISHED,
  Statuses.COMMITTED,
  Statuses.CANCELLED,
  Statuses.SENT
];

export const StatusesArray: TPOStatus[] = Object.keys(Statuses) as TPOStatus[];

export const VisibleStatuses: TPOStatus[] = StatusesArray.filter(
  (s) => !HiddenStatuses.includes(s)
);

export const StatusesOptions = VisibleStatuses.map((status) => {
  return {
    key: status,
    label: `planningOrderStatuses.${status}`
  };
});

export const ResourceName: IResourceName = {
  singular: 'planningOrder',
  plural: 'planningOrders'
};

export const Commands = {
  UpdatePlanningOrder: 'UpdatePlanningOrder',
  LaunchOne: 'LaunchOne',
  DraftOne: 'DraftOne',
  CommitOne: 'CommitOne',
  SendOne: 'SendOne',
  FinishOne: 'FinishOne',
  DeleteOne: 'DeleteOne',
  SplitOne: 'SplitOne',
  CreateDraftOrder: 'CreateDraftOrder',
  CreateDraftOrderFromSku: 'CreateDraftOrderFromSku',
  ChangeManyRoadmaps: 'ChangeManyRoadmaps',
  LaunchMany: 'LaunchMany',
  CommitMany: 'CommitMany',
  SendMany: 'SendMany',
  FinishMany: 'FinishMany',
  DeleteMany: 'DeleteMany',
  ExplodeMany: 'ExplodeMany',
  DraftProjections: 'DraftProjections',
  DraftPlanningProjections: 'DraftPlanningProjections',

  UpdateOrderNum: 'UpdateOrderNum',
  UpdateExternalData: 'UpdateExternalData',
  ChangeRoadmap: 'ChangeRoadmap'
};

export const Events = {
  PlanningOrdersDrafted: 'PlanningOrdersDrafted',
  PlanningOrdersLaunched: 'PlanningOrdersLaunched',
  PlanningOrdersSent: 'PlanningOrdersSent',
  PlanningOrdersCommitted: 'PlanningOrdersCommitted',
  PlanningOrderDeleted: 'PlanningOrderDeleted',
  PlanningOrderCancelled: 'PlanningOrderCancelled',
  PlanningOrdersDeleted: 'PlanningOrdersDeleted',
  PlanningOrdersFinished: 'PlanningOrdersFinished',
  PlanningOrderCreated: 'PlanningOrderCreated',
  PlanningOrderUpdated: 'PlanningOrderUpdated',
  PlanningOrderSplit: 'PlanningOrderSplit',
  PlanningOrderCommitted: 'PlanningOrderCommitted',
  PlanningOrderSent: 'PlanningOrderSent',
  PlanningOrderFinished: 'PlanningOrderFinished',
  PlanningOrdersOrderNumChanged: 'PlanningOrdersOrderNumChanged',
  PlanningOrdersExternalDataChanged: 'PlanningOrdersExternalDataChanged',
  PlanningOrderRoadmapChanged: 'PlanningOrderRoadmapChanged',
  PlanningOrderRoadmapsChanged: 'PlanningOrderRoadmapsChanged',
  PlanningOrderExternalDataChanged: 'PlanningOrderExternalDataChanged'
};

export const POsLifecycleEvents = [
  Events.PlanningOrdersLaunched,
  Events.PlanningOrdersSent,
  Events.PlanningOrdersCommitted,
  Events.PlanningOrdersDrafted,
  Events.PlanningOrdersOrderNumChanged,
  Events.PlanningOrdersExternalDataChanged
];

export const DeletedEvents = [
  Events.PlanningOrderDeleted,
  Events.PlanningOrderCancelled,
  Events.PlanningOrderFinished
];

export const CreatedEvents = [
  Events.PlanningOrderCreated,
  Events.PlanningOrderUpdated,
  Events.PlanningOrderSplit,
  Events.PlanningOrderCommitted,
  Events.PlanningOrderSent,
  Events.PlanningOrderRoadmapChanged
];

export const Queries = {
  Users: 'users',
  WorkshopsCapacity: 'workshopsCapacity'
};

export const ExportableColumns = [
  'voltaOrderNum',
  'voltaOrderLine',
  'orderNum',
  'orderLine',
  'orderType',
  'warehouseCode',
  'partCode',
  'acqCode',
  'status',
  'reorderQty',
  'dueDate',
  'reorderDate',
  'deliveryDate',
  'supplierCode',
  'workshopCode',
  'deliveryWorkshopCode',
  'planningPriority',
  'executionPriority',
  'externalOrderNum',
  'unitPrice'
];

const formatDate = (d: string | Date | moment.Moment) => moment(d).format('YYYY-MM-DD');

const noId = 'zzzzzzzz-zzzz-zzzz-zzzz-zzzzzzzzzzzz';
const noOrderNum = '00000000';

type NormalizeHook = HookClass<any, PlanningOrder[], typeof PlanningOrder>;
type NormalizeHookSingle = HookClass<any, PlanningOrder, typeof PlanningOrder>;

export default class PlanningOrder extends BaseModel {
  // TODO - Remove when investigation on
  // why it only disappears on Planning Order model is done
  static modelName = 'planning-order';

  /**
   * Collection commands
   */

  static createDraftOrderFromSku = collectionCommand<ICreateDraftOrderFromSku, PlanningOrder>(Commands.CreateDraftOrderFromSku, {
    after: _normalize as NormalizeHookSingle
  });
  static draftProjections = collectionCommand<IChangeManyStatus, PlanningOrder[]>(Commands.DraftProjections, {
    after: _normalize as NormalizeHook
  });
  static draftPlanningProjections = collectionCommand<IDraftPlanningProjections, PlanningOrder[]>(Commands.DraftPlanningProjections, {
    after: _normalize as NormalizeHook
  });
  static createDraftOrder = collectionCommand<ICreateDraftOrder, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.CreateDraftOrder);
  static changeManyRoadmaps = collectionCommand<IChangeManyRoadmaps, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.ChangeManyRoadmaps);
  static launchMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.LaunchMany);
  static commitMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.CommitMany);
  static sendMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.SendMany);
  static finishMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.FinishMany);
  static deleteMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.DeleteMany);
  static explodeMany = collectionCommand<IChangeManyStatus, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.ExplodeMany);
  static updateOrderNum = collectionCommand<IUpdateOrderNum, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.UpdateOrderNum);
  static updateExternalData = collectionCommand<IUpdateExternalData, ICmdResult<IManyPlanningOrdersUpdatedResult>>(Commands.UpdateExternalData);

  /**
   * Resource commands
   */

  updatePlanningOrder = command<IUpdatePlanningOrder, PlanningOrder>(Commands.UpdatePlanningOrder);
  launchOne = command<IChangeOneStatus, PlanningOrder>(Commands.LaunchOne);
  draftOne = command<IChangeOneStatus, PlanningOrder>(Commands.DraftOne);
  commitOne = command<IChangeOneStatus, PlanningOrder>(Commands.CommitOne);
  sendOne = command<IChangeOneStatus, PlanningOrder>(Commands.SendOne);
  finishOne = command<IChangeOneStatus, PlanningOrder>(Commands.FinishOne);
  deleteOne = command<undefined, PlanningOrder>(Commands.DeleteOne);
  split = command<IChangeOneStatus, PlanningOrder>(Commands.SplitOne);
  changeRoadmap = command<IChangeRoadmap, PlanningOrder>(Commands.ChangeRoadmap);

  /**
   * Planning id
   */
  @attr('string') planningId!: string;

  /**
   * Reorder quantity
   */
  @attr('number') reorderQty!: number;

  /**
   * Due date
   */
  @attr('date') dueDate!: Date;

  /**
   * Reorder date (due date - dlt)
   */
  @attr('date') reorderDate!: Date;

  /**
   * Internal due date (due date - ilt)
   */
  @attr('date') deliveryDate!: Date;

  /**
   * Optimal Flow Date (Next stock out date - 1)
   */
  @attr('date') optimalFlowDate?: Date;

  /**
   *  acqCode Acquisition code
   */
  @attr('string') acqCode!: TAcquisitionCode;

  /**
   * Status
   */
  @attr('string') status!: TPOStatus;

  /**
   *  PO order type
   */
  @attr('string') orderType?: string | 'STANDARD';

  /**
   *  VOLTA generated order num
   */
  @attr('string') voltaOrderNum?: string;

  /**
   *  VOLTA generated order line
   */
  @attr('number') voltaOrderLine?: number;

  /**
   *  Internal order num
   */
  @attr('string') orderNum?: string;

  /**
   *  Internal order line
   */
  @attr('string') orderLine?: string;

  /**
   *  External order num
   */
  @attr('string') externalOrderNum?: string;

  /**
   * Overridden unit price
   */
  @attr('number') unitPrice?: number;

  /**
   * Workshop capacity production cadence
   */
  @attr('number') cadence?: number;

  /**
   * User id of the creator
   */
  @attr('string') createdBy?: string;

  /**
   * User id of the last updator
   */
  @attr('string') updatedBy?: string;

  /**
   * Condensed Planning data
   */
  @attr() planningInfo!: { bufferPenetration?: number; bufferZone?: number };

  /**
   * Condensed Execution data
   */
  @attr() executionInfo!: { oha?: number; ohaZone?: number };

  /**
   * Condensed SKU data
   */
  @attr() skuInfo!: IPlanningOrderSKU;

  /**
   * Reorder quantity converted in multiple units
   */
  @attr() convertedQties!: Record<string, number>;

  /**
   * Aggregated stats like comments count
   */
  @attr() stats!: { commentsCount: number };

  /**
   * Condensed delivery Workshop data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  deliveryWorkshopInfo?: IWorkshopIdentityInfo;

  /**
   * Condensed supplier data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  supplierInfo?: ISupplierIdentityInfo;

  /**
   * Condensed workshop data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  workshopInfo?: IWorkshopIdentityInfo;

  /**
   * Condensed alerts info
   */
  @attr() alerts!: { scheduling?: ISchedulingAlertSummary; synchronisation?: ISyncAlertSummary };

  /**
   * Aggregated data about each task type
   */
  @attr() tasks?: IAggTasksViewMap;

  // Computed Properties
  // ~~~~

  get dueDateOptimal() {
    return this.optimalFlowDate;
  }

  get reorderDateOptimal() {
    return this.optimalFlowDate
      ? moment(this.optimalFlowDate).subtract(this.skuInfo.dlt, 'days').toDate()
      : undefined;
  }

  get deliveryDateOptimal() {
    return this.optimalFlowDate
      ? moment(this.optimalFlowDate)
        .subtract(this.skuInfo.ilt ?? 0.0, 'days')
        .toDate()
      : undefined;
  }

  get dueDateIsOptimal() {
    return moment(this.dueDate).isSame(this.optimalFlowDate, 'days');
  }

  get stockOutDate() {
    return this.optimalFlowDate ? moment(this.optimalFlowDate).add(1, 'days').toDate() : undefined;
  }

  /**
   * Comments count
   */
  get commentsCount() {
    return this.tasks?.COMMENT?.total;
  }

  /**
   * Sku id
   */
  get skuId() {
    return this.skuInfo.skuId;
  }

  /**
   * Delivery workshop id
   * @type {?string}
   */
  get deliveryWorkshopId() {
    return this.deliveryWorkshopInfo?.workshopId;
  }

  /**
   * Supplier id
   * @type {?string}
   */
  get supplierId() {
    return this.supplierInfo?.supplierId;
  }

  /**
   * Workshop id
   * @type {?string}
   */
  get workshopId() {
    return this.workshopInfo?.workshopId;
  }

  /**
   * Warehouse id
   * @type {?string}
   */
  get warehouseId() {
    return this.skuInfo.warehouse.warehouseId;
  }

  /**
   * Is the PO in DRAFT
   * @type {boolean}
   */
  get isDraft() {
    return this.status === 'DRAFT';
  }

  get isFirm() {
    return FirmPOStatuses.includes(this.status);
  }

  /**
   * Planning priority name
   */
  get priorityName() {
    const { bufferZone } = this.planningInfo;
    return bufferZone ? getBufferZoneCriticality(bufferZone) : undefined;
  }

  /**
   * Execution priority name
   */
  get executionPriorityName() {
    const { ohaZone } = this.executionInfo;
    return ohaZone ? getBufferZoneCriticality(ohaZone) : undefined;
  }

  /**
   * The priority is used for sorting by status
   */
  get priority() {
    const index = StatusesArray.indexOf(this.status);
    return index === -1 ? 6 : index;
  }

  /**
   * Reorder quantity as a monetary value
   * @type {number}
   */
  get reorderQtyValue() {
    return this.reorderQty * (this.actualUnitPrice ?? 0);
  }

  /**
   * Actual order number
   * @type {?string}
   */
  get num() {
    return this.orderNum || this.voltaOrderNum;
  }

  /**
   * Is the PO a Distribution Order
   * @type {boolean}
   */
  get isDistribute() {
    return this.acqCode === 'DISTRIBUTE';
  }

  /**
   * Generated Key used for grouping PO as logical blocks
   */
  get orderKey(): string {
    const { num, warehouseId, acqCode, supplierId, deliveryWorkshopId, workshopId, isDistribute } =
      this;

    return `${num || noOrderNum}_${warehouseId}_${acqCode}_${supplierId || noId}_${
      workshopId || noId
    }_${(isDistribute && deliveryWorkshopId) || noId}`;
  }

  get orderHash() {
    return `${hashCode(this.orderKey)}`;
  }

  /**
   * Object containing the grouping key with all its constituents
   */
  get orderKeyObject(): IPOKeyObject {
    return {
      orderHash: this.orderHash,
      orderKey: this.orderKey,
      num: this.num,
      warehouse: this.skuInfo.warehouse,
      acqCode: this.acqCode,
      supplier: this.supplierInfo,
      workshop: this.workshopInfo,
      deliveryWorkshop: this.isDistribute ? this.deliveryWorkshopInfo : undefined,
      externalOrderNum: this.externalOrderNum
    };
  }

  get actualUnitPrice() {
    return this.unitPrice ?? this.skuInfo.unitPrice;
  }

  /**
   * Builds a flat data structure exportable as a csv
   */
  toExportable(): Record<string, any> {
    const {
      skuInfo,
      deliveryWorkshopInfo,
      workshopInfo,
      supplierInfo,
      executionPriorityName,
      priorityName,
      reorderQty,
      dueDate,
      acqCode,
      status,
      orderType,
      voltaOrderNum,
      voltaOrderLine,
      orderNum,
      orderLine,
      reorderDate,
      deliveryDate,
      optimalFlowDate,
      externalOrderNum,
      actualUnitPrice
    } = this;
    return {
      orderType,
      voltaOrderNum,
      voltaOrderLine,
      orderNum,
      orderLine,
      warehouseCode: skuInfo.warehouse.code,
      partCode: skuInfo.part.code,
      acqCode,
      status,
      reorderQty,
      dueDate: formatDate(dueDate),
      reorderDate: formatDate(reorderDate),
      deliveryDate: formatDate(deliveryDate),
      optimalFlowDate: optimalFlowDate ? formatDate(optimalFlowDate) : undefined,
      supplierCode: supplierInfo?.code,
      workshopCode: workshopInfo?.code,
      deliveryWorkshopCode: deliveryWorkshopInfo?.code,
      planningPriority: priorityName,
      executionPriority: executionPriorityName,
      externalOrderNum,
      unitPrice: actualUnitPrice
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'planning-order': PlanningOrder;
  }
}
